import { useMemo } from 'react';

import { useAppSelector } from '../../../store';
import { findKeyTabById } from '../lib/getTabById';
import { getTags } from '../lib/getTags';
import { isOverdueTask } from '../lib/isOverdueTask';
import { fallbackTaskColor } from '../model/consts';
import { tasksSelector } from '../model/slice/taskSlice';
import { systemTaskColor } from '../model/types/systemTaskColors';
import { Task } from '../model/types/tasks';

interface ListTaskInfo {
    tagsFormattedToEditStore: string[];
    taskTags: string[];
    projectAndEstimateName?: string | null;
    taskColor: string;
    isOverdue: boolean;
}

export const useGetListTaskInfo = (taskId: string): ListTaskInfo => {
    const tasksInformationData = useAppSelector((state) => state.tasksInformationReducer);
    const taskData: Task = useAppSelector((state) => tasksSelector.selectById(state, taskId));
    const tabs = useAppSelector((state) => state.tasksInformationReducer.tabs);

    const isOverdue = isOverdueTask(taskData?.date_end);
    const isSystemTask = taskData?.type === 'system';

    const taskTags = useMemo(() => {
        if (!taskData?.uid || tasksInformationData.tags.length === 0) return [];
        return getTags(taskData, tasksInformationData);
    }, [taskData?.uid, tasksInformationData.tags]);

    const taskColor = useMemo(() => {
        if (!taskData) return undefined;
        const currentTab = findKeyTabById(tabs.board, taskData.uid) as keyof typeof systemTaskColor;

        if (!isSystemTask) {
            return isOverdue && !taskData.completed
                ? '#C1281B'
                : taskData.context?.color || fallbackTaskColor;
        }
        if (isSystemTask && taskData.completed) return '#28B57A';
        if (isSystemTask && isOverdue) return '#C1281B';
        return (currentTab && systemTaskColor[currentTab]) || fallbackTaskColor;
    }, [taskData, tabs.board, isSystemTask, isOverdue]);

    const tagsFormattedToEditStore = tasksInformationData.tags
        .map((tag) => (taskData?.context?.tags?.includes(tag.id) ? tag.title : null))
        .filter(Boolean);

    return { tagsFormattedToEditStore, taskTags, taskColor, isOverdue };
};
